
import { format } from "date-fns";
import { ChartDataPoint, PuttingResult } from "@/types/puttingResults";

/**
 * Prepares chart data from putting results
 * @param results - Array of putting results
 * @param locale - Current language locale for date formatting
 */
export const prepareChartData = (results: PuttingResult[], locale?: string): ChartDataPoint[] => {
  if (results.length === 0) return [];
  
  return results
    .slice()
    .sort((a, b) => new Date(a.test_date).getTime() - new Date(b.test_date).getTime())
    .map(result => ({
      date: format(new Date(result.test_date), locale === 'en' ? 'MM/dd' : 'dd/MM'),
      points: result.points,
      level: result.test_level || 'N/A',
      testName: result.test_name || 'Standard',
      testId: result.test_id || ''
    }));
};

/**
 * Groups chart data by test name/type
 * @param results - Array of putting results
 * @param locale - Current language locale for date formatting
 */
export const groupChartDataByTest = (results: PuttingResult[], locale?: string) => {
  if (results.length === 0) return {};
  
  const chartData = prepareChartData(results, locale);
  const groupedData: Record<string, ChartDataPoint[]> = {};
  
  // Group data by test name or ID
  chartData.forEach(dataPoint => {
    const key = dataPoint.testName || dataPoint.testId || 'Standard';
    if (!groupedData[key]) {
      groupedData[key] = [];
    }
    groupedData[key].push(dataPoint);
  });
  
  return groupedData;
};

/**
 * Returns a list of tests found in the results
 */
export const getUniqueTestTypes = (results: PuttingResult[]): string[] => {
  if (results.length === 0) return [];
  
  const testNames = new Set<string>();
  
  results.forEach(result => {
    const testName = result.test_name || result.test_id || 'Standard';
    testNames.add(testName);
  });
  
  return Array.from(testNames);
};
